import { Filter, FilterType, FilterValue } from '@dfl/mui-admin-layout';
import { EmptyFilter, ExistFilter, OperatorFilter, RangeFilter, TermFilter } from '@dofleini/query-builder';
import { ORDER_STATUSES_ALL_KEY } from 'modules/sales/settings/order-status/constants';
import { DELIVERY_MAX_TIME_ENUM, DELIVERY_TIME_TYPE_ENUM } from './order.enum';
import { getOfferCouponFilter, OFFER_COUPON_VALUES } from './order-offer.filters';
import { ProductService } from 'modules/inventory/product/services';
import { LOGISTICS_LIST_KEY } from 'modules/inventory/provider/logistics/constants';
import { LogisticsService } from 'modules/inventory/provider/logistics/services';
import { transformWhitObjectId } from 'modules/common/constants/object-id';
import { DistributionCentersService } from 'modules/inventory/distribution-centers/services';
import { DISTRIBUTION_CENTERS_LIST_KEY } from 'modules/inventory/distribution-centers/constants';
import { deliveryMaxTimeFilterTransform } from '../utils/order-delivery-max-time-transforms';
import { PAYMENT_GATEWAYS_ENUM, PAYMENT_METHOD_LIST_KEY } from 'modules/sales/settings/payment-settings/constants';
import { OrderStatusService } from 'modules/sales/settings/order-status/services';
import { PaymentMethodsService } from 'modules/sales/settings/payment-settings/services';
import { addressFilters } from 'lib/dfl-address-filter/filters';
import { sanitizeTextFilter } from 'utils/search.utils';
import { SUPPLIER_LIST_KEY } from 'modules/inventory/provider/supplier/constants';
import { SupplierService } from 'modules/inventory/provider/supplier/services';

export const paymentMethodFilter = (field?: string): Filter => ({
  filter: 'order:payment.method.title',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'paymentMethod',
  field: field ?? 'payment.paymentMethod',
  fetchFunc: PaymentMethodsService.search,
  labelKey: 'methodType',
  fetchOption: { size: 200 },
  queryKey: PAYMENT_METHOD_LIST_KEY,
  valueKey: 'methodType',
  mapEntities: (rows) =>
    rows?.map((row) => ({
      ...row,
      label: `order:payment.method.${row?.methodType as string}`,
      value: row?.methodType,
      translate: true,
    })),
});

export const paymentGatewayFilter: Filter = {
  filter: 'order:payment.gateway.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'gateway',
  field: 'payment.gateway',
  options: Object.values(PAYMENT_GATEWAYS_ENUM)?.map((gateway) => ({
    value: gateway,
    translate: true,
    label: `order:payment.gateway:${gateway}`,
  })),
};

export const orderCodeFilter: Filter = {
  filter: 'order:code',
  translate: true,
  field: 'code',
  key: 'code',
  placeholder: 'COD',
  type: FilterType.TEXT,
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'code',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

export const orderOnDemandHoursFilter: Filter = {
  filter: 'order:shipping.onDemandHours',
  translate: true,
  field: 'shipping.onDemandHours',
  key: 'deliveryTime',
  type: FilterType.NUMBER,
  transform: (value) => {
    if (!value) return;
    if (Array.isArray(value)) return new EmptyFilter();
    const [fromRaw, toRaw] = String(value).split('_');

    const from = fromRaw ? Number(fromRaw) : undefined;
    const to = toRaw ? Number(toRaw) : undefined;

    if (from !== undefined && to === undefined) {
      return new RangeFilter({
        field: 'shipping.onDemandHours.from',
        from,
      }).toQuery();
    }

    if (from === undefined && to !== undefined) {
      return new RangeFilter({
        field: 'shipping.onDemandHours.to',
        to,
      }).toQuery();
    }

    if (from !== undefined && to !== undefined) {
      const fromFilter = new RangeFilter({
        field: 'shipping.onDemandHours.from',
        from,
        to: from,
      }).toQuery();

      const toFilter = new RangeFilter({
        field: 'shipping.onDemandHours.to',
        from: to,
        to,
      }).toQuery();

      return new OperatorFilter({ type: 'OR', filters: [fromFilter, toFilter] }).toQuery();
    }

    return new EmptyFilter();
  },
};

export const orderCodeProductFilter: Filter = {
  filter: 'product:warehouseStockModal.codeProduct',
  translate: true,
  field: 'items.productSnapShot.code',
  key: 'p-code',
  type: FilterType.TEXT,
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'items.productSnapShot.code',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

export const orderStatusFilter: Filter = {
  filter: 'order:status.title',
  translate: true,
  key: 'status',
  field: 'status._id',
  labelKey: 'title',
  type: FilterType.DYNAMIC_LIST,
  queryKey: ORDER_STATUSES_ALL_KEY,
  fetchFunc: OrderStatusService.search,
  transform: (value) => transformWhitObjectId(value, 'status._id'),
};

export const orderDeliverTimeTypeFilter: Filter = {
  filter: 'order:shipping.deliveryTimeType.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'deliveryTimeType',
  field: 'shipping.timeType',
  options: Object.keys(DELIVERY_TIME_TYPE_ENUM)?.map((delivery) => ({
    value: delivery,
    translate: true,
    label: `order:shipping.deliveryTimeType.${delivery}.title`,
  })),
};

export const orderOfferFilter: Filter = {
  filter: 'order:offers.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'offers',
  field: 'offers',
  transform: (value: FilterValue) => {
    const opt = Array.isArray(value) ? value : [value];
    if (opt) {
      return new OperatorFilter({
        type: 'OR',
        filters: opt?.map((value) => getOfferCouponFilter(value as OFFER_COUPON_VALUES)),
      });
    }
    return new EmptyFilter();
  },
  options: [
    {
      value: OFFER_COUPON_VALUES.OFFER_TRUE,
      translate: true,
      label: 'order:offers.active',
    },
    {
      value: OFFER_COUPON_VALUES.OFFER_FALSE,
      translate: true,
      label: 'order:offers.inActive',
    },
    {
      value: OFFER_COUPON_VALUES.COUPON_TRUE,
      translate: true,
      label: 'order:offers.coupon',
    },
    {
      value: OFFER_COUPON_VALUES.COUPON_FALSE,
      translate: true,
      label: 'order:offers.notCoupon',
    },
  ],
};

export const orderQuantityFilter: Filter = {
  filter: 'order:quantity',
  translate: true,
  type: FilterType.NUMBER,
  key: 'items',
  field: 'totalProducts',
};

export const orderDeliveryDueDateFilter: Filter = {
  filter: 'order:shipping.deliveryDueDate',
  translate: true,
  type: FilterType.DATE,
  key: 'deliveryDueDate',
  field: 'shipping.deliveryDueDate',
};

export const orderPaymentDateFilter: Filter = {
  filter: 'order:payment.paidAt',
  translate: true,
  type: FilterType.DATE,
  key: 'paidAt',
  field: 'payment.paidAt',
};

export const orderOnDemandDateFilter: Filter = {
  filter: 'order:shipping.onDemandDate',
  translate: true,
  type: FilterType.DATE,
  key: 'onDemandDate',
  field: 'shipping.onDemandDate',
};

export const orderDeliveryDateFilter: Filter = {
  filter: 'order:shipping.deliveredAt',
  translate: true,
  type: FilterType.DATE,
  key: 'deliveryDate',
  field: 'shipping.deliveryDate',
};

export const orderChargeBackDateFilter: Filter = {
  filter: 'order:billing.chargeBackDate',
  translate: true,
  type: FilterType.DATE,
  key: 'chargeBackDate',
  field: 'billing.chargeBackDate',
};

export const orderProductItemsFilter: Filter = {
  filter: 'order:items.products',
  translate: true,
  type: FilterType.DYNAMIC_LIST,
  key: 'items.product',
  field: 'items.product',
  fetchOption: { size: 200 },
  fetchFunc: ProductService.search,
  mapEntities: (item: any[]) => {
    const finalProductMapper: any[] = [];
    item.map((item: any) =>
      finalProductMapper.push({
        value: item._id,
        label: item.name.length > 20 ? String(item.name).substring(0, 40) + '...' : item.name,
        item,
      }),
    );
    return finalProductMapper;
  },
  transform: (value) => {
    if (Array.isArray(value)) {
      return new OperatorFilter({
        type: 'OR',
        filters: value?.map(
          (e) =>
            new OperatorFilter({
              type: 'OR',
              filters: [
                new TermFilter({ field: 'items.product', value: e, objectId: true }),
                new TermFilter({
                  field: 'items.productSnapShot.variantOf',
                  value: e,
                  objectId: true,
                }),
              ],
            }),
        ),
      });
    }
    return new TermFilter({ field: 'items.product', value, objectId: true });
  },
};

export const orderHasPaymentFilter: Filter = {
  filter: 'order:payment.paid',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'paid',
  field: 'payment.paid',
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'true':
        return new TermFilter({ field: 'payment.paid', value: true }).toQuery();
      case 'false':
        return new TermFilter({ field: 'payment.paid', value: { $exists: false } }).toQuery();
    }
  },
  options: [
    {
      value: 'true',
      translate: true,
      label: 'order:hasPayment.true',
    },
    {
      value: 'false',
      translate: true,
      label: 'order:hasPayment.false',
    },
  ],
};

export const orderEmailFilter: Filter = {
  filter: 'clients:email',
  translate: true,
  field: 'client.email',
  key: 'email',
  type: FilterType.TEXT,
  transform: (value: any) => {
    const queryText = sanitizeTextFilter(value);
    if (!queryText) return undefined;
    return new TermFilter({
      field: 'client.email',
      value: {
        $regex: queryText,
        $options: 'i',
      },
    });
  },
};

export const orderInformationFilter: Filter = {
  filter: 'order:information.label',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'paymentStatus',
  field: 'payment.information.fraudCatName',
  options: [
    {
      value: 'FRAUD',
      translate: true,
      label: 'order:information.FRAUD',
    },
    {
      value: 'SUSPICIOUS',
      translate: true,
      label: 'order:information.SUSPICIOUS',
    },
    {
      value: 'NO_FRAUD',
      translate: true,
      label: 'order:information.NO_FRAUD',
    },
  ],
};

export const orderTotalItemsFilter: Filter = {
  filter: 'order:totalItems',
  field: 'totalQuantity',
  translate: true,
  type: FilterType.NUMBER,
  key: 'i_count',
};

export const orderTotalProductsFilter: Filter = {
  filter: 'order:totalProducts',
  field: 'totalProducts',
  translate: true,
  type: FilterType.NUMBER,
  key: 'p_count',
};

export const orderProductsAmountFilter: Filter = {
  filter: 'order:filter.productsAmount',
  field: 'invoice.details.products.value',
  translate: true,
  type: FilterType.NUMBER,
  key: 'p_amount',
};

export const orderTotalAmountFilter: Filter = {
  filter: 'order:invoice.total',
  field: 'invoice.total',
  translate: true,
  type: FilterType.NUMBER,
  key: 'total',
};

export const orderDeliveryDueFilter: Filter = {
  filter: 'order:shipping.deliveryMaxTime.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'deliveryMaxTime',
  field: 'shipping.deliveryDueDate',
  options: [
    {
      value: DELIVERY_MAX_TIME_ENUM.TIME,
      translate: true,
      label: 'order:shipping.deliveryMaxTime.TIME',
    },
    {
      value: DELIVERY_MAX_TIME_ENUM.RISK,
      translate: true,
      label: 'order:shipping.deliveryMaxTime.RISK',
    },
    {
      value: DELIVERY_MAX_TIME_ENUM.LATE,
      translate: true,
      label: 'order:shipping.deliveryMaxTime.LATE',
    },
    {
      value: DELIVERY_MAX_TIME_ENUM.SEVERE,
      translate: true,
      label: 'order:shipping.deliveryMaxTime.SEVERE',
    },
    {
      value: DELIVERY_MAX_TIME_ENUM.CRITICS,
      translate: true,
      label: 'order:shipping.deliveryMaxTime.CRITICS',
    },
  ],
  transform: (value) => deliveryMaxTimeFilterTransform({ value, field: 'shipping.deliveryDueDate' }),
};

export const orderSupplierFilter: Filter = {
  filter: 'common:provider.SUPPLIER',
  translate: true,
  key: 'supplier',
  field: 'items.productSnapShot.providers.supplier._id',
  queryKey: SUPPLIER_LIST_KEY,
  type: FilterType.DYNAMIC_LIST,
  fetchFunc: SupplierService.search,
  fetchOption: { size: 200 },
  mapEntities: (item: any[]) => {
    return item.map((a) => ({
      value: a._id,
      label: a.name,
    }));
  },
  transform: (value) => transformWhitObjectId(value, 'items.productSnapShot.providers.supplier._id'),
};

export const orderLogisticFilter: Filter = {
  filter: 'order:logisticProvider',
  translate: true,
  key: 'logistic',
  field: 'distributionCenter.logistic._id',
  queryKey: LOGISTICS_LIST_KEY,
  type: FilterType.DYNAMIC_LIST,
  fetchOption: { size: 200 },
  fetchFunc: LogisticsService.search,
  mapEntities: (item: any[]) => {
    return item.map((a) => ({
      value: a._id,
      label: a.name,
    }));
  },
  transform: (value) => transformWhitObjectId(value, 'distributionCenter.logistic._id'),
};

export const orderDistributionCenterFilter: Filter = {
  filter: 'distributionCenters:name',
  translate: true,
  key: 'dc',
  field: 'distributionCenter._id',
  queryKey: DISTRIBUTION_CENTERS_LIST_KEY,
  type: FilterType.DYNAMIC_LIST,
  fetchOption: { size: 200 },
  fetchFunc: DistributionCentersService.search,
  transform: (value) => transformWhitObjectId(value, 'distributionCenter._id'),
};

export const orderWeightFilter: Filter = {
  filter: 'order:shippingPackage.weight',
  translate: true,
  type: FilterType.NUMBER,
  key: 'weight',
  field: 'shippingSettings.shippingInfo.weight',
};

export const orderVolumeFilter: Filter = {
  filter: 'order:shippingPackage.volume',
  translate: true,
  type: FilterType.NUMBER,
  key: 'volume',
  field: 'shippingSettings.shippingInfo.volume',
};

export const orderInDispatchFilter: Filter = {
  filter: 'dispatch:inDispatch',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'in-dispatch',
  field: 'dispatch',
  options: [
    {
      value: 'IN_DISPATCH',
      translate: true,
      label: 'dispatch:inDispatch',
    },
    {
      value: 'NO_DISPATCH',
      translate: true,
      label: 'dispatch:noDispatch',
    },
  ],
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'IN_DISPATCH':
        return new OperatorFilter({
          type: 'OR',
          filters: [new ExistFilter({ field: 'dispatch', value: true })],
        });
      case 'NO_DISPATCH':
        return new TermFilter({ field: 'dispatch', value: null });
      default:
        return new EmptyFilter();
    }
  },
};

export const orderCarrierFilter: Filter = {
  filter: 'common:provider.CARRIER',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'carrier',
  field: 'carrier',
  options: [
    {
      value: 'CARRIER',
      translate: true,
      label: 'common:yes',
    },
    {
      value: 'NO_CARRIER',
      translate: true,
      label: 'common:no',
    },
  ],
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'CARRIER':
        return new OperatorFilter({
          type: 'OR',
          filters: [
            new ExistFilter({ field: 'carrier', value: true }),
            new ExistFilter({ field: 'paymentAgreement', value: true }),
          ],
        });
      case 'NO_CARRIER':
        return new OperatorFilter({
          type: 'AND',
          filters: [
            new TermFilter({ field: 'carrier', value: null }),
            new TermFilter({ field: 'paymentAgreement', value: null }),
          ],
        });
      default:
        return new EmptyFilter();
    }
  },
};

export const orderInPaymentAgreementFilter: Filter = {
  filter: 'paymentAgreement:inPaymentAgreement',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'paymentAgreement',
  field: 'paymentAgreement',
  options: [
    {
      value: 'IN_PAYMENT_AGREEMENT',
      translate: true,
      label: 'paymentAgreement:inPaymentAgreement',
    },
    {
      value: 'NO_PAYMENT_AGREEMENT',
      translate: true,
      label: 'paymentAgreement:noPaymentAgreement',
    },
  ],
  transform: (value) => {
    if (Array.isArray(value)) return new EmptyFilter();
    switch (value) {
      case 'IN_PAYMENT_AGREEMENT':
        return new OperatorFilter({
          type: 'OR',
          filters: [new ExistFilter({ field: 'paymentAgreement', value: true })],
        });
      case 'NO_PAYMENT_AGREEMENT':
        return new TermFilter({ field: 'paymentAgreement', value: null });
      default:
        return new EmptyFilter();
    }
  },
};

export const orderIncidencesFilter: Filter = {
  filter: 'breadcrumb:incidences',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'incidences',
  field: 'metrics.totalIncidences',
  transform: (value: FilterValue) => {
    if (value === OFFER_COUPON_VALUES.OFFER_TRUE) {
      return new TermFilter({ field: 'metrics.totalIncidences', value: { $gt: 0 } });
    }
    if (value === OFFER_COUPON_VALUES.OFFER_FALSE) {
      return new TermFilter({ field: 'metrics.totalIncidences', value: 0 });
    }
    return new EmptyFilter();
  },
  options: [
    {
      value: OFFER_COUPON_VALUES.OFFER_TRUE,
      translate: true,
      label: 'order:filter.incidence',
    },
    {
      value: OFFER_COUPON_VALUES.OFFER_FALSE,
      translate: true,
      label: 'order:filter.noIncidence',
    },
  ],
};

export const orderActionTakenFilter: Filter = {
  filter: 'order:filter.actionTaken.title',
  translate: true,
  type: FilterType.FIXED_LIST,
  key: 'actionTaken',
  field: 'actionTaken',
  options: [
    {
      value: 'CLIENT_CALLED',
      translate: true,
      label: 'order:filter.actionTaken.CLIENT_CALLED',
    },
    {
      value: 'CLIENT_REACHED',
      translate: true,
      label: 'order:filter.actionTaken.CLIENT_REACHED',
    },
    {
      value: 'null',
      translate: true,
      label: 'order:filter.actionTaken.NO_ACTION',
    },
  ],

  transform: (value) => {
    const ACTION_TAKEN_OPTIONS = ['CLIENT_CALLED', 'CLIENT_REACHED', 'null'];
    const buildFilter = (v: string) => {
      switch (v) {
        case 'CLIENT_CALLED':
          return new OperatorFilter({
            type: 'OR',
            filters: [
              new TermFilter({ field: 'actionTaken', value: 'CLIENT_CALLED' }),
              new TermFilter({ field: 'actionTaken', value: true }),
            ],
          });
        case 'CLIENT_REACHED':
          return new TermFilter({ field: 'actionTaken', value: 'CLIENT_REACHED' });
        case 'null':
          return new OperatorFilter({
            type: 'OR',
            filters: [
              new ExistFilter({ field: 'actionTaken', exists: false }),
              new TermFilter({ field: 'actionTaken', value: false }),
              new TermFilter({ field: 'actionTaken', value: null }),
              new TermFilter({ field: 'actionTaken', value: 'NO_ACTION' }),
            ],
          });
        default:
          return null;
      }
    };

    const values = Array.isArray(value) ? value : [value];
    if (values.length === 0 || values.length >= ACTION_TAKEN_OPTIONS.length) return new EmptyFilter();

    const filters = values.map(buildFilter).filter((f): f is OperatorFilter | TermFilter => f !== null);
    if (filters.length === 0) return new EmptyFilter();
    if (filters.length === 1) return filters[0].toQuery();
    return new OperatorFilter({ type: 'OR', filters }).toQuery();
  },
};

// address filters
export const shippingAddressFilter = addressFilters({
  countryFilter: 'warehouse:address.country',
  countryKey: 'country',
  countryField: 'shipping.address.country',
  stateFilter: 'warehouse:address.state',
  stateKey: 'state',
  stateField: 'shipping.address.state',
  cityFilter: 'warehouse:address.city',
  cityKey: 'city',
  cityField: 'shipping.address.city',
  justUseFor: 'CU',
});
